import { Injectable } from '@angular/core';

import { AuthFacade } from '@cpd-app/state/auth/auth.facade';
import {
  Observable,
  BehaviorSubject,
  combineLatest,
  defer,
  of
} from 'rxjs';
import { catchError, map, retry, take, timeout } from 'rxjs/operators';
import {
  MilitaryBranchesGQL,
  ExtendedMilitaryBranchesGQL,
  SearchRanksGQL,
  SearchMocsGQL,
  MilitaryServeType,
  MilitaryServeTypeGQL,
  AirbaseLocationsGQL,
  AirbaseLocation,
  SpacebaseLocation,
  SpacebaseLocationsGQL,
  InstallationLocation,
  InstallationLocationsGQL
} from '@cpd-app/generated';

import {
  MilitaryBranch,
  Rank,
  ExtendedMilitaryBranch,
  MocSearch
} from '../../models';

/**
 * What a component lookup came back with.
 *
 * `denied` and an empty `types` are different outcomes: the first means the
 * caller has no permission to read the list, the second means the branch has
 * no components. They looked identical before, because this query reports a
 * denial as HTTP 200 with an `errors` array and the list arrives empty either
 * way, so the screen told a signed-out member their branch had no components.
 */
@Injectable({
  providedIn: 'root'
})
export class MilitaryBranchService {
  private militaryBranchListSubject = new BehaviorSubject<MilitaryBranch[]>(
    undefined
  );
  private militaryServeTypesSubject = new BehaviorSubject<MilitaryServeType[]>(
    undefined
  );
  private airbaseLocationsSubject = new BehaviorSubject<AirbaseLocation[]>(
    undefined
  );
  private spacebaseLocationsSubject = new BehaviorSubject<SpacebaseLocation[]>(
    undefined
  );
  private installationLocationsSubject = new BehaviorSubject<
    InstallationLocation[]
  >(undefined);
  private extendedMilitaryBranchListSubject = new BehaviorSubject<
    ExtendedMilitaryBranch[]
  >(undefined);
  private filteredMocListSubject = new BehaviorSubject<MocSearch[]>(undefined);
  private filteredRankListSubject = new BehaviorSubject<Rank[]>(undefined);

  public militaryBranchList$ = this.militaryBranchListSubject.asObservable();
  public militaryServeTypes$ = this.militaryServeTypesSubject.asObservable();
  public airbaseLocations$ = this.airbaseLocationsSubject.asObservable();
  public spacebaseLocations$ = this.spacebaseLocationsSubject.asObservable();
  public installationLocations$ =
    this.installationLocationsSubject.asObservable();
  public extendedMilitaryBranchList$ =
    this.extendedMilitaryBranchListSubject.asObservable();
  public filteredMocList$ = this.filteredMocListSubject.asObservable();
  public filteredRankList$ = this.filteredRankListSubject.asObservable();

  constructor(
    private militaryBranchesGQL: MilitaryBranchesGQL,
    private militaryServeTypeGQL: MilitaryServeTypeGQL,
    private airbaseLocationsGQL: AirbaseLocationsGQL,
    private spacebaseLocationsGQL: SpacebaseLocationsGQL,
    private installationLocationsGQL: InstallationLocationsGQL,
    private extendedMilitaryBranchesGQL: ExtendedMilitaryBranchesGQL,
    private searchRanksGQL: SearchRanksGQL,
    private searchMocsGQL: SearchMocsGQL,
    private authFacade: AuthFacade
  ) {}

  public getBranchList(): Observable<MilitaryBranch[]> {
    return new Observable((observer) => {
      this.militaryBranchesGQL
        .fetch()
        .pipe(
          map((results) => {
            const branchList: MilitaryBranch[] = [];
            results?.data?.militaryBranches.map((item: MilitaryBranch) =>
              branchList.push({
                branchCode: item.branchCode,
                branchTitle: item.branchTitle,
                branchLabel: item.branchLabel,
                mocSubstitute: item.mocSubstitute
              })
            );

            this.militaryBranchListSubject.next(branchList);
            observer.next(branchList);
            observer.complete();
          }),
          // The branch-list resolver waits on this observable, so a failed
          // query used to leave the route unactivated and the page blank.
          // An empty list is a page that says it has no branches.
          catchError(() => {
            this.militaryBranchListSubject.next([]);
            observer.next([]);
            observer.complete();
            return of(undefined);
          })
        )
        ?.subscribe();
    });
  }

  /**
   * The components for a branch, pushed to the shared subject the survey and
   * the old Build Your Target wizard both read.
   *
   * THIS IS THE THIRD ATTEMPT AT THE SAME SYMPTOM, so the two that missed are
   * worth keeping. The select would not fill, and because step one cannot be
   * left without a component, the survey could not be started.
   *
   *   - The first attempt read it as the shared subject losing the response
   *     between callers, and returned an owned stream instead. It was reverted.
   *   - The second read it as a permissions race and made the query wait for
   *     the bearer token, then retry a denial four times. Neither helped.
   *
   * Both investigations recorded that "the query returned all four components
   * with a 200 every time", and one of them explicitly ruled out "the
   * `sortByAsc` prototype extension (always present)". That was true and it was
   * the wrong half of the line. The problem was the ASSIGNMENT two lines above
   * it, `item['softIndex'] = ...`, writing a scratch field onto Apollo's
   * response so it could be sorted by it. See `fetchServeTypes`.
   */
  public getMilitaryServeTypes(branchCode: string) {
    this.fetchServeTypes(branchCode)
      .pipe(take(1))
      .subscribe((types) => this.militaryServeTypesSubject.next(types));
  }

  /**
   * IT WROTE ON THE RESPONSE, AND THAT IS WHY IT NEVER ANSWERED.
   *
   * The Component select sat on "Loading components…" indefinitely, and before
   * the loading state was wired it sat on "No components found, try the branch
   * again" -- the same failure wearing different words, because the list simply
   * never arrived. Step one cannot be left without a component, so the survey
   * could not be started at all.
   *
   * Reproduced against the local build on 22 September: choosing Army sent
   * EXACTLY ONE request and then nothing. No retry, no result, no error on
   * screen, still unresolved minutes later. One request rules out the retry
   * loop; a permanent "Loading…" rules out every failure path, because all of
   * them end in an emission.
   *
   * The old code did this to the response:
   *
   *     militaryServeType.forEach((item, index) => {
   *       item['softIndex'] = item.name === 'Veteran' ? lastIndex : index;
   *     });
   *     militaryServeType.sortByAsc((item) => item['softIndex']);
   *
   * Apollo hands back FROZEN objects in a development build: `readFromStore`
   * calls `maybeDeepFreeze`, whose body is `if (__DEV__) deepFreeze(obj)`.
   * Assigning `softIndex` onto a frozen object throws a TypeError in strict
   * mode, which every emitted ES module is. That throw happens in the LAST
   * `map`, below `retry` and below `catchError`, so nothing caught it, nothing
   * retried it, and `next` was never reached. The subject kept its initial
   * `undefined` for good.
   *
   * It is also why this looked fine on the deployed environment and broke
   * locally: a production build sets `__DEV__` false, so the same assignment
   * succeeds there. Not an environment quirk -- the same latent bug with the
   * seatbelt off.
   *
   * So the response is copied before anything touches it, and the ordering is
   * a comparator rather than a scratch field written onto somebody else's
   * object. `sortByAsc` is not needed here either.
   *
   * ALWAYS EMITS, EXACTLY ONCE. A select that cannot say what happened is what
   * made this so hard to see. The deadline and the catch both answer with an
   * empty list, which the step draws as "No components found, try the branch
   * again": wrong is recoverable, silent is not.
   */
  private fetchServeTypes(
    branchCode: string
  ): Observable<MilitaryServeType[]> {
    return defer(() =>
      // network-only, so a retry asks again rather than instantly replaying a
      // cached answer.
      this.militaryServeTypeGQL.fetch(
        { branchCode },
        { fetchPolicy: 'network-only', errorPolicy: 'all' }
      )
    ).pipe(
      map((results) => {
        // COPIED FIRST. See above: the response is frozen in a dev build.
        const types = [...(results?.data?.militaryServeType ?? [])];

        // Veteran last; everything else keeps the order the server sent.
        return types.sort(
          (a, b) =>
            (a?.name === 'Veteran' ? 1 : 0) - (b?.name === 'Veteran' ? 1 : 0)
        ) as MilitaryServeType[];
      }),
      retry({ count: 2, delay: 700 }),
      // A hard deadline, because "still loading" is not a state this select is
      // allowed to finish in.
      timeout({
        first: 10000,
        with: () => of([] as MilitaryServeType[])
      }),
      catchError(() => of([] as MilitaryServeType[]))
    );
  }

  public getExtendedBranchList(): Observable<ExtendedMilitaryBranch[]> {
    return new Observable((observer) => {
      this.extendedMilitaryBranchesGQL
        .fetch()
        .pipe(
          map((results) => {
            const extendedBranchList: ExtendedMilitaryBranch[] = [];

            results?.data?.extendedMilitaryBranches.map(
              (item: ExtendedMilitaryBranch) =>
                extendedBranchList.push({
                  branchCode: item.branchCode,
                  extendedBranchCode: item.extendedBranchCode,
                  extendedBranchTitle: item.extendedBranchTitle,
                  mocSubstitute: item.mocSubstitute
                })
            );

            this.extendedMilitaryBranchListSubject.next(extendedBranchList);

            observer.next(extendedBranchList);
            observer.complete();
          })
        )
        ?.subscribe();
    });
  }

  public searchRankList(militaryBranch: MilitaryBranch, searchTerm: string) {
    const data = {
      branchCode: militaryBranch.branchCode,
      searchTerm
    };

    this.searchRanksGQL
      .fetch(data)
      .pipe(
        map((results) => {
          const ranks: Rank[] = results?.data?.searchRanks.map((rank) => {
            return {
              payGrade: rank.payGrade,
              title: rank.title,
              abbreviation: rank.abbreviation,
              branchCode: rank.branchCode,
              mpcCode: rank.mpcCode,
              payGradeLevel: rank.payGradeLevel
            };
          });

          this.filteredRankListSubject.next(ranks);
        }),
        catchError(() => {
          this.filteredRankListSubject.next([]);
          return of(undefined);
        })
      )
      .subscribe();
  }

  public searchMocList(
    branchCode: string,
    searchTerm: string,
    extendedBranchCode: string = null,
    activeType: string,
    mpcCode: string = null
  ) {
    const data = {
      branchCode,
      searchTerm,
      extendedBranchCode,
      activeType,
      mpcCode
    };

    this.searchMocsGQL
      .fetch(data)
      .pipe(
        map((results) => {
          const mocs: MocSearch[] = results?.data?.searchMocs.map((moc) => {
            return {
              mocCode: moc.mocCode,
              mocTitle: moc.mocTitle,
              branchCode: moc.branchCode,
              extendedBranchCode: moc.extendedBranchCode,
              mpcCode: moc.mpcCode,
              status: moc.status,
              hasEnlisted: moc.hasEnlisted,
              hasWarrantOfficer: moc.hasWarrantOfficer,
              hasCommissionOfficer: moc.hasCommissionOfficer
            };
          });

          this.filteredMocListSubject.next(mocs);
        }),
        // A failed search empties the list rather than killing the subject.
        // Without this the first error left `filteredMocList$` silent for the
        // rest of the session, so every later search looked like no results.
        catchError(() => {
          this.filteredMocListSubject.next([]);
          return of(undefined);
        })
      )
      .subscribe();
  }

  public getAirbaseLocations(searchTerm: string) {
    this.airbaseLocationsGQL
      .fetch({ searchTerm })
      .pipe(
        map((results) => {
          this.airbaseLocationsSubject.next(
            results?.data?.airbaseLocations || []
          );
        })
      )
      .subscribe();
  }

  public getSpacebaseLocations(searchTerm: string) {
    this.spacebaseLocationsGQL
      .fetch({ searchTerm })
      .pipe(
        map((results) => {
          this.spacebaseLocationsSubject.next(
            results?.data?.spacebaseLocations || []
          );
        })
      )
      .subscribe();
  }

  /**
   * DATA-1: all-branch installation search. branchCode is required by the
   * query - the backend returns that branch's installations plus the
   * branch-agnostic ones (joint bases, HQ/agency sites). Server-side this is
   * gated on INSTALLATION_CAPTURE_ENABLED, so an errored response while the
   * flag is off must not wipe a previous list into null: emit [] instead.
   */
  public getInstallationLocations(branchCode: string, searchTerm: string) {
    this.installationLocationsGQL
      .fetch({ branchCode, searchTerm })
      .pipe(
        map((results) => {
          this.installationLocationsSubject.next(
            results?.data?.installationLocations || []
          );
        }),
        catchError(() => {
          this.installationLocationsSubject.next([]);
          return of(undefined);
        })
      )
      .subscribe();
  }

  public clearSearchMocList() {
    this.filteredMocListSubject.next(undefined);
  }

  public clearSearchRankList() {
    this.filteredRankListSubject.next(undefined);
  }
}
